import React, { useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import PhoneInput from "react-phone-input-2";
import { Autocomplete } from "@react-google-maps/api";
import { Dropdown } from "primereact/dropdown";
import { toast } from "react-toastify";
import FieldHelpTooltip from "../../../../components/Help/FieldHelpTooltip";
import { HELP_CONTENT } from "../../../../components/Help/HelpContent";
import { realProviderNames } from "../../../../utils/bookingAssignment";
import { getBookingLabel } from "../../../../utils/industryLabels";
import { formatStepAssignments } from "../../workflow/workflowAssignments";

const Required = () => <span style={{ color: "red" }}>*</span>;

// Step 1: who the booking is for - mobile number (looks the customer up and fills the rest),
// contact details and, where the industry needs it, the address.
function CustomerStep({ b, formik }) {
  const {
    isSportsService,
    isCarService,
    isSalonService,
    currency,
    editable,
    industry,
    debouncedCheckUserExists,
    isLoaded,
    onLoad,
    onPlaceChanged,
    locationError,
    states,
    countries,
    handleStateChange,
    handleCountryChange,
    recentBookings,
    user_id
  } = b;
  const { values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched, setValues } = formik;

  // Car, sports and salon visits don't need an address: tucked away unless there is one (or an error in it).
  const addressOptional = isSportsService || isCarService || isSalonService;
  const [addressOpen, setAddressOpen] = useState(false);
  const hasAddressValue = ["house_no", "area", "location", "state", "landmark", "lat", "lng"].some((f) => values[f]);
  const hasAddressError = ["house_no", "area", "location", "state", "country", "lat", "lng"].some((f) => touched[f] && errors[f]);
  const showAddress = !addressOptional || addressOpen || hasAddressValue || hasAddressError || !!locationError;
  const requiredMark = !addressOptional;

  const digits = (values.phoneNumber || "").length;
  const lookedUp = digits >= 12;

  return (
    <div className="bstep-card">
      <div className="bstep-card-head">
        <h5>{isSportsService ? "Player details" : "Customer details"}</h5>
        <p>Enter the mobile number first - an existing customer is found and filled in for you.</p>
      </div>

      <Row className="g-3 mb-2" data-tour="booking-phone">
        <Col md={6}>
          <Form.Group>
            <Form.Label>
              Mobile Number <Required />
              <FieldHelpTooltip {...(isSportsService ? HELP_CONTENT.bookings.SportsphoneNumber : HELP_CONTENT.bookings.phoneNumber)} />
            </Form.Label>
            <PhoneInput
              country={currency === "INR" ? "in" : "ae"}
              onlyCountries={["ae", "in"]}
              value={values.phoneNumber}
              disabled={!editable}
              onChange={(value) => {
                const cleanedValue = value.replace(/\D/g, "");
                setFieldValue("phoneNumber", cleanedValue);
                setFieldTouched("phoneNumber", true, false);

                // Only check for user existence if the number is complete
                if (cleanedValue.length >= 12) {
                  debouncedCheckUserExists("phoneNumber", cleanedValue, setFieldValue, setValues);
                }
              }}
              inputStyle={{
                width: "100%",
                height: "40px",
                backgroundColor: "white",
                borderColor: touched.phoneNumber && errors.phoneNumber ? "#dc3545" : "#E9ECEF"
              }}
              inputProps={{ name: "phoneNumber" }}
            />
            {touched.phoneNumber && errors.phoneNumber && (
              <div className="invalid-feedback d-block">{errors.phoneNumber}</div>
            )}
            {lookedUp && !errors.phoneNumber && (
              <div className={`bstep-chip ${user_id ? "is-found" : "is-new"}`} role="status">
                {user_id ? "Existing customer - details filled in" : "New customer"}
              </div>
            )}
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label>
              {isSportsService ? "Player Name" : "Customer Name"} <Required />
              <FieldHelpTooltip {...(isSportsService ? HELP_CONTENT.bookings.SportscustomerName : HELP_CONTENT.bookings.customerName)} />
            </Form.Label>
            <Form.Control
              type="text"
              name="userName"
              placeholder={isSportsService ? "Enter Player Name..." : "Enter Customer Name..."}
              value={values.userName}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={!editable}
              isInvalid={touched.userName && errors.userName}
              className="form-input"
            />
            {touched.userName && errors.userName && <Form.Control.Feedback type="invalid">{errors.userName}</Form.Control.Feedback>}
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label>
              Email Address
              <FieldHelpTooltip {...HELP_CONTENT.bookings.email} />
            </Form.Label>
            <Form.Control
              type="email"
              name="email"
              placeholder="Enter email address"
              value={values.email}
              disabled={!editable}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={touched.email && errors.email}
              className="form-input"
            />
            {touched.email && errors.email && <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>}
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label>
              Origin {getBookingLabel(industry)} ID
              <FieldHelpTooltip {...HELP_CONTENT.bookings.originBookingId} />
            </Form.Label>
            <Form.Control
              type="text"
              name="org_id"
              placeholder={`Enter original ${getBookingLabel(industry).toLowerCase()} ID...`}
              value={values.org_id}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={!editable}
              isInvalid={touched.org_id && errors.org_id}
              className="form-input"
              style={{ height: "40px" }}
            />
            {touched.org_id && errors.org_id && <div className="invalid-feedback d-block">{errors.org_id}</div>}
          </Form.Group>
        </Col>
      </Row>

      <div className="bstep-divider" />

      <div className="d-flex align-items-center justify-content-between mb-2" data-tour="booking-address">
        <h6 className="section-heading mb-0">
          Full Address
          {addressOptional && <span className="bstep-muted"> (optional)</span>}
        </h6>
        {addressOptional && !hasAddressValue && !hasAddressError && (
          <button type="button" className="btn btn-link p-0" style={{ fontSize: "13px" }} onClick={() => setAddressOpen((v) => !v)}>
            {showAddress ? "Hide address" : "Add address"}
          </button>
        )}
      </div>

      {showAddress && (
        <Row className="g-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>
                Address {requiredMark && <Required />}
                <FieldHelpTooltip {...(isSportsService ? HELP_CONTENT.bookings.Sportsaddress : HELP_CONTENT.bookings.address)} />
              </Form.Label>

              <div className="d-flex align-items-center gap-2">
                <div style={{ flex: 1 }}>
                  {isLoaded && (
                    <Autocomplete
                      onLoad={onLoad}
                      onPlaceChanged={() => onPlaceChanged(setFieldValue)}
                      options={{ componentRestrictions: { country: currency === "INR" ? "in" : "ae" } }}
                    >
                      <Form.Control
                        type="text"
                        name="location"
                        placeholder="Search address"
                        value={values.location}
                        onChange={handleChange}
                        className="form-input"
                      />
                    </Autocomplete>
                  )}
                </div>

                {values.location && (
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    aria-label="Clear address"
                    onClick={() => {
                      setFieldValue("location", "");
                      setFieldValue("area", "");
                      setFieldValue("state", "");
                      setFieldValue("lat", "");
                      setFieldValue("lng", "");
                    }}
                  >
                    ✖
                  </button>
                )}
              </div>

              {locationError && <div className="invalid-feedback d-block">{locationError}</div>}
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label>
                House / Flat / Building No {requiredMark && <Required />}
                <FieldHelpTooltip {...(isSportsService ? HELP_CONTENT.bookings.Sportshouseno : HELP_CONTENT.bookings.houseNo)} />
              </Form.Label>
              <Form.Control
                type="text"
                name="house_no"
                placeholder="Enter house / flat / building no"
                value={values.house_no}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={!editable}
                isInvalid={touched.house_no && errors.house_no}
                className="form-input"
                style={{ height: "40px" }}
              />
              {touched.house_no && errors.house_no && <div className="invalid-feedback d-block">{errors.house_no}</div>}
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label>
                Area {requiredMark && <Required />}
                <FieldHelpTooltip {...(isSportsService ? HELP_CONTENT.bookings.Sportsarea : HELP_CONTENT.bookings.area)} />
              </Form.Label>
              <Form.Control
                type="text"
                name="area"
                placeholder="Enter area details..."
                value={values.area}
                onChange={handleChange}
                disabled={!editable}
                onBlur={handleBlur}
                isInvalid={touched.area && errors.area}
                className="form-input"
                style={{ height: "40px" }}
              />
              {touched.area && errors.area && <div className="invalid-feedback d-block">{errors.area}</div>}
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label>
                {currency === "INR" ? "State" : "Emirate"} {requiredMark && <Required />}
                <FieldHelpTooltip {...HELP_CONTENT.bookings.state} />
              </Form.Label>
              <Dropdown
                name="state"
                value={values.state}
                options={states?.map((s) => ({ label: s.name, value: s.name }))}
                placeholder={currency === "INR" ? "Select State" : "Select Emirate"}
                disabled={!editable}
                onChange={(e) => {
                  handleStateChange(e, setFieldValue);
                  setFieldValue("state", e.value);
                }}
                onBlur={handleBlur}
                className={`w-100 ${touched.state && errors.state ? "is-invalid" : ""}`}
                style={{
                  height: "40px",
                  backgroundColor: "white",
                  border: touched.state && errors.state ? "1px solid #dc3545" : "1px solid #E9ECEF"
                }}
              />
              {touched.state && errors.state && <div className="invalid-feedback d-block">{errors.state}</div>}
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label>
                Landmark
                <FieldHelpTooltip {...(isSportsService ? HELP_CONTENT.bookings.Sportslandmark : HELP_CONTENT.bookings.landmark)} />
              </Form.Label>
              <Form.Control
                type="text"
                name="landmark"
                placeholder="Enter landmark details..."
                value={values.landmark}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={!editable}
                isInvalid={touched.landmark && errors.landmark}
                className="form-input"
                style={{ height: "40px" }}
              />
              {touched.landmark && errors.landmark && <div className="invalid-feedback d-block">{errors.landmark}</div>}
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label>
                Country
                <FieldHelpTooltip {...(isSportsService ? HELP_CONTENT.bookings.Sportscountry : HELP_CONTENT.bookings.country)} />
              </Form.Label>
              <Dropdown
                name="country"
                value={currency === "INR" ? 2 : 1}
                options={countries?.map((c) => ({ label: c.name, value: Number(c.country_id) }))}
                placeholder="Select Country"
                disabled={!editable}
                onChange={(e) => {
                  const expectedCountryId = currency === "INR" ? 2 : 1;
                  if (Number(e.value) !== expectedCountryId) {
                    toast.error(
                      currency === "INR"
                        ? "Please select a country related to your profile (India)."
                        : "Please select a country related to your profile (UAE)."
                    );
                    return;
                  }
                  handleCountryChange(e, setFieldValue);
                  setFieldValue("country", e.value);
                }}
                onBlur={handleBlur}
                className={`w-100 ${touched.country && errors.country ? "is-invalid" : ""}`}
                style={{
                  height: "40px",
                  backgroundColor: "white",
                  border: touched.country && errors.country ? "1px solid #dc3545" : "1px solid #E9ECEF"
                }}
              />
              {touched.country && errors.country && <div className="invalid-feedback d-block">Please select a country</div>}
            </Form.Group>
          </Col>

          <Col md={12}>
            <Form.Group>
              <Form.Label>
                Longitude and Latitude
                <FieldHelpTooltip {...HELP_CONTENT.bookings.latLng} />
              </Form.Label>
              <Row className="g-2">
                <Col>
                  <Form.Control
                    type="text"
                    name="lat"
                    placeholder="Latitude"
                    value={values.lat}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={!editable}
                    isInvalid={touched.lat && errors.lat}
                    className="form-input"
                    style={{ fontSize: "14px", height: "40px" }}
                  />
                  {touched.lat && errors.lat && <div className="invalid-feedback d-block">{errors.lat}</div>}
                </Col>
                <Col>
                  <Form.Control
                    type="text"
                    name="lng"
                    placeholder="Longitude"
                    value={values.lng}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={!editable}
                    isInvalid={touched.lng && errors.lng}
                    className="form-input"
                    style={{ fontSize: "14px", height: "40px" }}
                  />
                  {touched.lng && errors.lng && <div className="invalid-feedback d-block">{errors.lng}</div>}
                </Col>
              </Row>
            </Form.Group>
          </Col>
        </Row>
      )}

      {recentBookings.length > 0 && (
        <>
          <div className="bstep-divider" />
          <h6 className="section-heading">Recent Bookings</h6>
          <div className="bstep-recent">
            <table className="bstep-grid">
              <thead>
                <tr>
                  <th>Booking Id</th>
                  <th>{isSportsService ? "Player Name" : "Customer Name"}</th>
                  <th>{isSportsService ? "Sports" : "Service"}</th>
                  <th>When</th>
                  <th className="is-amount">Amount</th>
                  <th>{isSportsService ? "Sports" : isCarService || isSalonService ? "Service Provider" : "Crew"}</th>
                </tr>
              </thead>
              <tbody>
                {recentBookings.map((item, index) => (
                  <tr key={index}>
                    <td className="is-nowrap">{item.display_booking_id}</td>
                    <td>{item.customer_name}</td>
                    <td>
                      <div>{item.service_name}</div>
                      {[item.service_type_name, item.service_offering].filter(Boolean).length > 0 && (
                        <div className="bstep-grid-sub">{[item.service_type_name, item.service_offering].filter(Boolean).join(" · ")}</div>
                      )}
                    </td>
                    <td className="is-nowrap">
                      <div>
                        {new Date(item.service_date).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric"
                        })}
                      </div>
                      <div className="bstep-grid-sub">{item.service_time}</div>
                    </td>
                    <td className="is-amount">{Number(item.total).toFixed(2)}</td>
                    <td>
                      {formatStepAssignments(item) ||
                        realProviderNames(item)
                          .split(",")
                          .filter(Boolean)
                          .map((name, i) => (
                            <span key={i} className="bstep-grid-pill">
                              {name.trim()}
                            </span>
                          ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default CustomerStep;
